<style lang="less" scoped>
  @import "../../../../style/theme.less";

</style>

<template>
  <div>
    <a-page-header
      @back="() => $router.go(-1)"
      title="Archetype Details"
      :sub-title="reportTypeDetails.descriptor"
      style="padding: 0; margin-bottom: 8px"
    >
      <template slot="extra">
        <a-button type="primary" shape="circle" icon="reload" @click="() => getReportTypeDetails({ id })"/>
      </template>
    </a-page-header>

    <a-row :gutter="[16, 16]">
      <a-col :span="10">
        <a-descriptions title="Archetype Information" bordered :column="1">
          <a-descriptions-item label="ID">
            <a-row :gutter="[16, 16]" type="flex">
              <a-col flex="auto">
                <span style="line-height: 32px">
                  {{ reportTypeDetails.id }}
                </span>
              </a-col>
              <a-col flex="50px">
                <a-popconfirm title="Are you sure you want to delete this archetype?" @confirm="() => removeReportType(reportTypeDetails.id).then(() => $router.go(-1))">
                  <a-button type="primary" shape="circle" icon="delete"/>
                </a-popconfirm>
              </a-col>
            </a-row>
          </a-descriptions-item>
          <a-descriptions-item label="Name">

            <a-row :gutter="[16, 16]" type="flex" v-if="!editReportTypeDescriptor">
              <a-col flex="auto">
                <span style="line-height: 32px">
                  {{ reportTypeDetails.descriptor }}
                </span>
              </a-col>
              <a-col flex="50px">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="edit"
                  @click="editDescriptor"
                />
              </a-col>
            </a-row>

            <a-row :gutter="[16, 16]" type="flex" v-else>
              <a-col flex="auto">
                <a-input
                  v-model="updatedDescriptor"
                  style="max-width: 100%"
                  placeholder="Name"
                />
              </a-col>
              <a-col flex="50px">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="save"
                  @click="() => patchReportType({ descriptor: updatedDescriptor })"
                />
              </a-col>
            </a-row>

          </a-descriptions-item>

          <a-descriptions-item label="Type">
            <a-row :gutter="[16, 16]" type="flex" v-if="!editReportTypeType">
              <a-col flex="auto">
                <span style="line-height: 32px">
                  {{ this.constants.report.typeDescriptors[reportTypeDetails.type] }}
                </span>
              </a-col>
              <a-col flex="50px">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="edit"
                  @click="editReportType"
                />
              </a-col>
            </a-row>

            <a-row :gutter="[16, 16]" type="flex" v-else>
              <a-col flex="auto">

                <a-select
                  style="width: 100%"
                  placeholder="Type"
                  v-model="updatedType"
                >
                  <a-select-option
                    v-for="type in constants.report.types"
                    :key="type"
                    :value="type"
                  >
                    {{ constants.report.typeDescriptors[type] }}
                  </a-select-option>
                </a-select>
              </a-col>
              <a-col flex="50px">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="save"
                  @click="() => patchReportType({ type: updatedType })"
                />
              </a-col>
            </a-row>
          </a-descriptions-item>

          <a-descriptions-item label="Created At">{{ reportTypeDetails.createdAt | timestamp }}</a-descriptions-item>
          <a-descriptions-item label="Updated At">{{ reportTypeDetails.updatedAt | timestamp }}</a-descriptions-item>
        </a-descriptions>
      </a-col>

      <a-col :span="14" v-if="reportTypeDetails.condition">
        <conditions-display
          title="Conditions"
          :conditions="reportTypeDetails.condition.conditions"
          :editable="true"
          @edit="openEditConditionsModal"
        />

        <a-modal
          title="Edit Conditions"
          :visible="editConditions"
          width="90%"
          @ok="saveEditConditionsModal"
          @cancel="closeEditConditionsModal"
        >
          <conditions-editor
            ref="conditionsEditor"
            :conditions="reportTypeDetails.condition.conditions"
            @change="conditions => updatedConditions = conditions"
          />
        </a-modal>

      </a-col>
    </a-row>

    <div class="spacer-lg" />

    <a-row :gutter="[16, 16]">
      <a-col :span="10">
        <report-blocks :reportBlocks="reportTypeDetails.reportBlocks" @edit="setReportBlockEditId" />
      </a-col>
      <a-col :span="14">
        <edit-block />
      </a-col>
    </a-row>
  </div>
</template>

<script>

import { mapState, mapActions, mapMutations } from 'vuex';

import conditionsDisplay from '../../../../components/shared/conditions/display';
import conditionsEditor from '../../../../components/shared/conditions/editor';
import reportBlocks from './_report-blocks';
import editBlock from './_edit-block';

export default {
  name: 'admin.report-types.details',

  data: () => ({
    id: null,

    editConditions: false,
    updatedConditions: null,
    updatedDescriptor: undefined,
    updatedType: undefined,
  }),

  components: {
    conditionsDisplay,
    conditionsEditor,
    reportBlocks,
    editBlock,
  },

  mounted() {
    this.id = this.$route.params.id;
    if (!this.id) {
      this.$router.push({ name:'admin.report-types', });
    }

    this.setReportBlockEditId(null);
    return this.getReportTypeDetails({ id: this.id, })
      .then(() => {
        if (this.$route.params.editBlockId) {
          this.setReportBlockEditId(this.$route.params.editBlockId);
        }

        return this.listConstants();
      });
  },

  computed: {
    ...mapState('admin', ['reportTypeDetails', 'editReportTypeDescriptor', 'editReportTypeType', 'constants',]),
  },

  methods: {
    ...mapActions('admin', ['getReportTypeDetails', 'patchReportType', 'listConstants', 'removeReportType',]),
    ...mapMutations('admin', ['setReportBlockEditId', 'setEditReportTypeDescriptor', 'setEditReportTypeType',]),

    editDescriptor() {
      this.setEditReportTypeDescriptor(true);
      this.updatedDescriptor = this.reportTypeDetails.descriptor;
    },
    editReportType() {
      this.setEditReportTypeType(true);
      this.updatedType = this.reportTypeDetails.type;
    },

    openEditConditionsModal() {
      this.updatedConditions = null;
      this.editConditions = true;
    },
    closeEditConditionsModal() {
      this.$refs.conditionsEditor.reset();
      this.editConditions = false;
    },
    async saveEditConditionsModal() {
      this.editConditions = false;
      await this.patchReportType({ conditions: this.updatedConditions, });
    },
  },
};
</script>
